import React, { useState } from 'react';
import { Mail, ArrowLeft, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { authService } from '../../services/auth';
import { useNavigate } from 'react-router-dom';

interface ForgotPasswordFormProps {
  onBack: () => void;
}

const ForgotPasswordForm = () => {
  const navigate = useNavigate()
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || isLoading) return;

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await authService.forgotPassword(email);
      setMessage(response.message);
      setEmail(''); // Clear email after successful submission
    } catch (err) {
      setError(err instanceof Error ? err.message : t('forgotPassword.genericError'));
    } finally {
      setIsLoading(false);
    }
  };

  const inputClasses = "w-full bg-white/10 border border-gray-500 text-white rounded-lg py-3 px-11 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200";
  const iconClasses = "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors duration-200";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <motion.div
        className="text-center space-y-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <div className="mx-auto w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center">
          <Mail className="w-8 h-8 text-blue-400" />
        </div>
        <h2 className="text-2xl font-bold text-white">
          {t('forgotPassword.title')}
        </h2>
        <p className="text-gray-400 text-sm max-w-sm mx-auto">
          {t('forgotPassword.description')}
        </p>
      </motion.div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="p-3 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg flex items-center gap-2"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}

          {message && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="p-3 bg-green-500/20 border border-green-500/50 text-green-200 rounded-lg flex items-center gap-2"
            >
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{message}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="relative group">
          <Mail className={`${iconClasses} group-hover:text-blue-400`} />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${inputClasses} group-hover:border-blue-400`}
            placeholder={t('forgotPassword.emailPlaceholder')}
            required
            disabled={isLoading}
          />
        </div>

        <motion.button
          type="submit"
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg font-semibold
                   hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-blue-500/25
                   disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden flex items-center justify-center gap-2"
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.98 }}
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : null}
          {isLoading ? t('forgotPassword.loading') : t('forgotPassword.submit')}
        </motion.button>

        <motion.button
          type="button"
          onClick={() => navigate('/app/auth/login')}
          disabled={isLoading}
          className="w-full text-gray-400 hover:text-white transition-colors duration-200 flex items-center justify-center gap-2 text-sm"
          whileHover={{ x: -5 }}
        >
          <ArrowLeft className="w-4 h-4" />
          {t('forgotPassword.backToLogin')}
        </motion.button>
      </form>
    </motion.div>
  );
};

export default ForgotPasswordForm;

export { ForgotPasswordForm }